import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { EventService } from 'src/app/core/services/event.service';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { characteristicIconFor, groupIconFor } from '../../utils/characteristic-icons.util';

export type CharacteristicValueType = 'BOOLEAN' | 'NUMBER' | 'SELECT';
export type CharacteristicFilter = 'all' | 'selected' | 'unfilled' | 'featured';

export interface PickerItem {
  id: number;
  groupId: number;
  control: string;
  valueControl: string;
  description: string;
  valueType: CharacteristicValueType;
  unit: string | null;
  minValue: number | null;
  maxValue: number | null;
  isFeatured: boolean;
  icon: string | null;
  options: { key: string; label: string }[];
  propertyTypeGroupIds: number[];
  usageCount: number;
  searchText: string;
}

export interface PickerGroup {
  id: number;
  description: string;
  icon: string | null;
  items: PickerItem[];
}

export interface PickerGroupView {
  group: PickerGroup;
  items: PickerItem[];
  selected: number;
  total: number;
  hidden: number;
  expanded: boolean;
  /** Dica do grupo: várias / quantidades / uma opção. */
  hint: 'multi' | 'number' | 'select';
}

/** Nº de itens visíveis por grupo antes do "mostrar mais". */
const COLLAPSED_LIMIT = 12;

/**
 * Seletor de características partilhado por imóvel e empreendimento.
 *
 * Mantém o contrato antigo com os hosts: o estado vive em
 * `propertyCharateristicsData.data` com as chaves `p{grupo}_char{id}` (truthy
 * quando selecionada) e `p{grupo}_char{id}_value` (quantidade ou chave da
 * opção). O separador Anúncio e a extração com IA leem/escrevem estas chaves.
 */
@Component({
  selector: 'app-charateristics',
  templateUrl: './charateristics.component.html',
  styleUrls: ['./charateristics.component.scss'],
})
export class CharateristicsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() dataToFormInitChar: any;
  @Input() submitted = false;
  @Input() propertyCharateristicsData: any;
  @Input() property: any;
  /** Separador Detalhes do host, para esconder o que não se aplica ao tipo. */
  @Input() detailsForm: any;
  @Input() dataToFormInitDetails: any;
  /** Evento que o host dispara para recolher os dados. */
  @Input() eventName = 'get-data-tab-charateristics';

  @ViewChild('formElementRef') formElementRef!: ElementRef;

  propertyCharateristicsForm!: FormGroup;
  groups: PickerGroup[] = [];
  view: PickerGroupView[] = [];
  searchTerm = '';
  filter: CharacteristicFilter = 'all';
  selectedCount = 0;
  hiddenByTypeCount = 0;
  activeGroupId: number | null = null;
  readonly = false;

  private expanded = new Set<number>();
  private itemsByControl = new Map<string, PickerItem>();
  private subscriptionSubmitted?: Subscription;
  private aiExtractedSub?: Subscription;
  private valueChangesSub?: Subscription;

  constructor(
    private eventService: EventService,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.groups = this.buildGroups();
    this.initForm();
    this.readonly = this.computeReadonly();
    this.refreshView();
    this.valueChangesSub = this.propertyCharateristicsForm.valueChanges
      .pipe(auditTime(80))
      .subscribe(() => this.refreshView());
  }

  ngAfterViewInit(): void {
    this.subscriptionSubmitted = this.eventService.subscribe(this.eventName, () =>
      this.syncOut(),
    );
    this.aiExtractedSub = this.eventService.subscribe('ai-doc-extracted', (data: any) =>
      this.applyAiExtraction(data),
    );
  }

  ngOnDestroy(): void {
    this.syncOut();
    this.subscriptionSubmitted?.unsubscribe();
    this.aiExtractedSub?.unsubscribe();
    this.valueChangesSub?.unsubscribe();
  }

  // ---------------------------------------------------------------- estado

  isSelected(item: PickerItem): boolean {
    return !!this.propertyCharateristicsForm.get(item.control)?.value;
  }

  valueOf(item: PickerItem): any {
    return this.propertyCharateristicsForm.get(item.valueControl)?.value ?? null;
  }

  toggle(item: PickerItem): void {
    if (this.readonly) return;
    const selected = this.isSelected(item);
    if (selected) {
      this.patch(item, false, null);
      return;
    }
    this.patch(item, true, this.defaultValueFor(item));
  }

  step(item: PickerItem, delta: number, event?: Event): void {
    event?.stopPropagation();
    if (this.readonly) return;
    const current = Number(this.valueOf(item)) || 0;
    const min = item.minValue ?? 1;
    const next = current + delta;
    if (next < min) {
      this.patch(item, false, null);
      return;
    }
    if (item.maxValue !== null && item.maxValue !== undefined && next > item.maxValue) return;
    this.patch(item, true, next);
  }

  setNumber(item: PickerItem, raw: any): void {
    if (this.readonly) return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || raw === '' || raw === null) {
      this.patch(item, false, null);
      return;
    }
    const min = item.minValue ?? 1;
    let next = Math.trunc(parsed);
    if (next < min) {
      this.patch(item, false, null);
      return;
    }
    if (item.maxValue !== null && item.maxValue !== undefined && next > item.maxValue) {
      next = item.maxValue;
    }
    this.patch(item, true, next);
  }

  chooseOption(item: PickerItem, key: string, event?: Event): void {
    event?.stopPropagation();
    if (this.readonly) return;
    this.patch(item, true, key);
  }

  clearAll(): void {
    if (this.readonly) return;
    const patch: Record<string, any> = {};
    for (const item of this.itemsByControl.values()) {
      patch[item.control] = false;
      patch[item.valueControl] = null;
    }
    this.propertyCharateristicsForm.patchValue(patch);
    this.refreshView();
  }

  setFilter(filter: CharacteristicFilter): void {
    this.filter = filter;
    this.refreshView();
  }

  onSearch(term: string): void {
    this.searchTerm = term ?? '';
    this.refreshView();
  }

  toggleExpanded(groupId: number): void {
    if (this.expanded.has(groupId)) this.expanded.delete(groupId);
    else this.expanded.add(groupId);
    this.refreshView();
  }

  /**
   * Faz scroll só no contentor do simplebar. `scrollIntoView` subiria todos os
   * contentores com scroll, incluindo o do modal, e escondia os separadores.
   */
  scrollTo(groupId: number): void {
    this.activeGroupId = groupId;
    const host: HTMLElement | undefined = this.formElementRef?.nativeElement;
    const target: HTMLElement | null = host?.querySelector(`#char-group-${groupId}`) ?? null;
    if (!host || !target) return;
    const scroller: HTMLElement | null = host.closest('.simplebar-content-wrapper');
    if (!scroller) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const toolbar: HTMLElement | null = host.querySelector('.char-picker-toolbar');
    const offset = (toolbar?.offsetHeight ?? 0) + 8;
    const top =
      target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - offset;
    scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  trackGroup(_: number, view: PickerGroupView): number {
    return view.group.id;
  }

  trackItem(_: number, item: PickerItem): number {
    return item.id;
  }

  trackOption(_: number, option: { key: string }): string {
    return option.key;
  }

  // ------------------------------------------------------------- privados

  private patch(item: PickerItem, selected: boolean, value: any): void {
    this.propertyCharateristicsForm.patchValue({
      [item.control]: selected,
      [item.valueControl]: selected ? value : null,
    });
    this.refreshView();
  }

  private defaultValueFor(item: PickerItem): any {
    if (item.valueType === 'NUMBER') return Math.max(item.minValue ?? 1, 1);
    if (item.valueType === 'SELECT') return item.options[0]?.key ?? null;
    return null;
  }

  private buildGroups(): PickerGroup[] {
    const groups: PickerGroup[] = [];
    this.itemsByControl.clear();
    for (const parent of this.dataToFormInitChar?.characteristics ?? []) {
      const items: PickerItem[] = (parent?.char ?? []).map((c: any) => {
        const item: PickerItem = {
          id: Number(c.id),
          groupId: Number(parent.id),
          control: `p${parent.id}_char${c.id}`,
          valueControl: `p${parent.id}_char${c.id}_value`,
          description: `${c.description ?? ''}`,
          valueType: (c.valueType as CharacteristicValueType) || 'BOOLEAN',
          unit: c.unit ?? null,
          minValue: c.minValue ?? null,
          maxValue: c.maxValue ?? null,
          isFeatured: !!c.isFeatured,
          icon: characteristicIconFor(c.icon, c.baseDescription ?? c.description),
          options: Array.isArray(c.options) ? c.options : [],
          propertyTypeGroupIds: Array.isArray(c.propertyTypeGroupIds)
            ? c.propertyTypeGroupIds.map(Number)
            : [],
          usageCount: Number(c.usageCount ?? 0),
          searchText: this.normalize(c.description),
        };
        this.itemsByControl.set(item.control, item);
        return item;
      });
      groups.push({
        id: Number(parent.id),
        description: `${parent.description ?? ''}`,
        icon: groupIconFor(parent.icon, parent.baseDescription ?? parent.description),
        items,
      });
    }
    return groups;
  }

  private initForm(): void {
    if (!this.propertyCharateristicsData) {
      this.propertyCharateristicsData = { data: {}, errors: [], valid: true };
    }
    const data = this.propertyCharateristicsData.data ?? {};
    const controls: Record<string, any> = {};
    for (const item of this.itemsByControl.values()) {
      const raw = data[item.control];
      const selected = Array.isArray(raw) ? !!raw[0] : !!raw;
      const rawValue = data[item.valueControl];
      const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
      controls[item.control] = [selected];
      controls[item.valueControl] = [
        selected ? this.coerceValue(item, value) : null,
      ];
    }
    this.propertyCharateristicsForm = this.formBuilder.group(controls);
  }

  /** Valores legados (importações) podem vir sem sentido; normaliza-os. */
  private coerceValue(item: PickerItem, value: any): any {
    if (item.valueType === 'NUMBER') {
      const n = Number(value);
      return Number.isFinite(n) && n > 0 ? Math.trunc(n) : this.defaultValueFor(item);
    }
    if (item.valueType === 'SELECT') {
      const key = value !== null && value !== undefined ? `${value}` : '';
      return item.options.some((o) => o.key === key) ? key : this.defaultValueFor(item);
    }
    return null;
  }

  private syncOut(): void {
    if (!this.propertyCharateristicsForm) return;
    this.propertyCharateristicsData.data = this.propertyCharateristicsForm.value;
    this.propertyCharateristicsData.errors = [];
    this.propertyCharateristicsData.valid = true;
  }

  private computeReadonly(): boolean {
    if (!this.property) return false;
    const master = this.permissionsService.checkMasterPermission(
      'EDIT_PROPERTIES',
      this.property?.store?.id,
    );
    const agent = this.permissionsService.checkAgentPermission(
      this.property?.mainAgent,
      'EDIT_PROPERTIES',
    );
    return !master && !agent;
  }

  /** Grupo de tipo de imóvel escolhido no separador Detalhes, se houver. */
  private currentTypeGroupId(): number | null {
    const data = this.detailsForm?.data;
    const typeId = data?.propertyType2 || data?.propertyType;
    if (!typeId) return null;
    const type = (this.dataToFormInitDetails?.propertyTypes ?? []).find(
      (t: any) => Number(t.id) === Number(typeId),
    );
    return type?.groupId ? Number(type.groupId) : null;
  }

  private applies(item: PickerItem, typeGroupId: number | null): boolean {
    if (!item.propertyTypeGroupIds.length || typeGroupId === null) return true;
    return item.propertyTypeGroupIds.includes(typeGroupId);
  }

  private matchesFilter(item: PickerItem, selected: boolean): boolean {
    switch (this.filter) {
      case 'selected':
        return selected;
      case 'unfilled':
        return !selected;
      case 'featured':
        return item.isFeatured || selected;
      default:
        return true;
    }
  }

  /**
   * Recalcula as listas que o template percorre. Fica pré-calculado para o
   * *ngFor não receber arrays novos em cada ciclo de deteção.
   */
  private refreshView(): void {
    const term = this.normalize(this.searchTerm);
    const typeGroupId = this.currentTypeGroupId();
    const canCollapse = !term && this.filter === 'all';
    let selectedCount = 0;
    let hiddenByType = 0;
    const view: PickerGroupView[] = [];

    for (const group of this.groups) {
      let total = 0;
      let selected = 0;
      const candidates: PickerItem[] = [];
      for (const item of group.items) {
        const isSelected = this.isSelected(item);
        if (isSelected) {
          selected += 1;
          selectedCount += 1;
        }
        if (!isSelected && !this.applies(item, typeGroupId)) {
          hiddenByType += 1;
          continue;
        }
        total += 1;
        if (term && !item.searchText.includes(term)) continue;
        if (!this.matchesFilter(item, isSelected)) continue;
        candidates.push(item);
      }
      if (!candidates.length) continue;

      // Selecionadas e destaques primeiro; o resto na ordem do catálogo.
      const ordered = [...candidates].sort((a, b) => {
        const sa = this.isSelected(a) ? 0 : a.isFeatured ? 1 : 2;
        const sb = this.isSelected(b) ? 0 : b.isFeatured ? 1 : 2;
        return sa - sb;
      });
      const expanded = this.expanded.has(group.id);
      const items =
        canCollapse && !expanded && ordered.length > COLLAPSED_LIMIT
          ? ordered.slice(0, COLLAPSED_LIMIT)
          : ordered;
      view.push({
        group,
        items,
        selected,
        total,
        hidden: canCollapse ? ordered.length - items.length : 0,
        expanded,
        hint: this.hintFor(group),
      });
    }

    this.view = view;
    this.selectedCount = selectedCount;
    this.hiddenByTypeCount = hiddenByType;
    if (this.activeGroupId === null && view.length) this.activeGroupId = view[0].group.id;
  }

  private hintFor(group: PickerGroup): 'multi' | 'number' | 'select' {
    if (group.items.length && group.items.every((i) => i.valueType === 'SELECT')) return 'select';
    if (group.items.some((i) => i.valueType === 'NUMBER')) return 'number';
    return 'multi';
  }

  private normalize(value: any): string {
    return `${value ?? ''}`
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[.,/\-]/g, ' ')
      .replace(/\s+/g, ' ')
      .toLowerCase()
      .trim();
  }

  // ------------------------------------------------------- extração com IA

  private applyAiExtraction(data: any): void {
    const characteristics = Array.isArray(data?.characteristics) ? data.characteristics : [];
    if (!characteristics.length || !this.propertyCharateristicsForm) return;
    const targets = characteristics
      .map((value: any) => this.normalize(value))
      .filter((value: string) => !!value);
    if (!targets.length) return;

    const patch: Record<string, any> = {};
    const animated: string[] = [];
    for (const item of this.itemsByControl.values()) {
      if (!item.searchText) continue;
      const matched = targets.some(
        (target: string) => item.searchText.includes(target) || target.includes(item.searchText),
      );
      if (!matched || this.isSelected(item)) continue;
      patch[item.control] = true;
      patch[item.valueControl] = this.defaultValueFor(item);
      animated.push(item.control);
    }
    if (!Object.keys(patch).length) return;
    this.propertyCharateristicsForm.patchValue(patch);
    this.syncOut();
    this.refreshView();
    setTimeout(() => this.highlightAiFields(animated));
  }

  private highlightAiFields(controlNames: string[]): void {
    const host = this.formElementRef?.nativeElement;
    if (!host) return;
    for (const name of controlNames) {
      const el = host.querySelector(`[data-control="${name}"]`);
      if (!el) continue;
      this.renderer.addClass(el, 'ai-field-fill');
      setTimeout(() => this.renderer.removeClass(el, 'ai-field-fill'), 1600);
    }
  }
}
